import { useMemo } from 'react';

import { Box, CircularProgress } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { parseISO } from 'date-fns';
import { CalendarClock } from 'lucide-react';

import { useLocale } from '@shared/hooks/locale';

import {
  CHART_LEGEND_SLOT_PROPS,
  CHART_TOOLTIP_SX,
  ChartEmptyState,
} from './ChartControls';
import { type Granularity, buildBuckets } from './chartUtils';

// Embeddable viewed-buyers chart, rendered inside the funnel's Viewed buyers
// drill-down. Each buyer contributes once, on their first successful viewing.
export const ListingViewingsBody = ({
  start,
  end,
  granularity,
  viewedBuyerDates,
  color,
  loading,
}: {
  start: Date;
  end: Date;
  granularity: Granularity;
  viewedBuyerDates: string[];
  color: string;
  loading: boolean;
}) => {
  const { t } = useLocale();

  const chart = useMemo(() => {
    const buckets = buildBuckets(start, end, granularity);
    const keyIndex = new Map(buckets.keys.map((key, index) => [key, index]));
    const values = buckets.keys.map(() => 0);
    for (const viewedAt of viewedBuyerDates) {
      const date = parseISO(viewedAt);
      if (date < buckets.start || date > buckets.end) {
        continue;
      }
      const bucketIndex = keyIndex.get(buckets.keyOf(date));
      if (bucketIndex == null) {
        continue;
      }
      values[bucketIndex] += 1;
    }
    return { labels: buckets.labels, values };
  }, [viewedBuyerDates, start, end, granularity]);

  if (loading) {
    return (
      <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (chart.values.every(value => value === 0)) {
    return (
      <ChartEmptyState
        icon={<CalendarClock size={22} />}
        message={t('No viewings in this period')}
      />
    );
  }

  return (
    <BarChart
      height={260}
      xAxis={[
        {
          data: chart.labels,
          scaleType: 'band',
          tickLabelStyle: { fontSize: 10 },
        },
      ]}
      series={[
        {
          data: chart.values,
          label: t('Viewed buyers'),
          color,
        },
      ]}
      margin={{ top: 16, right: 8, bottom: 24, left: 8 }}
      slotProps={{
        tooltip: { sx: CHART_TOOLTIP_SX },
        legend: CHART_LEGEND_SLOT_PROPS,
      }}
    />
  );
};
